import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, TextField, MenuItem, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, CircularProgress, Grid, Dialog, DialogTitle, DialogContent,
  DialogActions, InputAdornment, IconButton, Tooltip, Divider,
} from '@mui/material';
import {
  Add, Refresh, Delete, ArrowBack,
  CheckCircle, Cancel, HourglassEmpty, Receipt,
} from '@mui/icons-material';
import { InvokeApi, apiList } from '../../services/ApiService';
import { Config } from '../../config/Config';
import ClassSectionGrid from '../../components/ClassSectionGrid';
import dayjs from 'dayjs';

const STATUS_TABS = [
  { key: 'Success', label: 'Paid', bg: '#e8f5e9', color: '#2e7d32', icon: <CheckCircle fontSize="small" /> },
  { key: 'Pending', label: 'Pending', bg: '#fff3e0', color: '#e65100', icon: <HourglassEmpty fontSize="small" /> },
  { key: 'Failed', label: 'Failed', bg: '#ffebee', color: '#c62828', icon: <Cancel fontSize="small" /> },
];

export default function FeesStructure() {
  const [selectedAcYear, setSelectedAcYear] = useState('');
  const [allStructures, setAllStructures] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [feesData, setFeesData] = useState({});
  const [structureData, setStructureData] = useState({});
  const [transactions, setTransactions] = useState([]);
  const [txMode, setTxMode] = useState('Success');
  const [detailLoading, setDetailLoading] = useState(false);
  const [dialog, setDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sections, setSections] = useState([]);
  const [form, setForm] = useState({
    standard: '', section: '',
    registrationFee: '0', admissionFee: '0', transportFee: '0', miscellaneousFee: '0',
    academic_year: '', status: 'Active', createdBy: '1',
  });

  useEffect(() => {
    const init = async () => {
      const [secData, acData] = await Promise.all([
        InvokeApi(Config.main_url + apiList.getDeptartmentSection, { status: 'Active' }),
        InvokeApi(Config.main_url + apiList.getAvailableAcademicYears, {}),
      ]);
      setSections(secData?.sections || []);
      const years = acData?.academic_years || [];
      setAcademicYears(years);
      const current = years.find(y => y.is_current) || years[0];
      if (current) {
        setSelectedAcYear(current.value || '');
        setForm(f => ({ ...f, academic_year: current.value || '' }));
      }
    };
    init();
  }, []);

  const loadStructures = useCallback(async () => {
    if (!selectedAcYear) return;
    const data = await InvokeApi(Config.main_url + apiList.getFeesStructureMain, {
      status: 'Active', academic_year: selectedAcYear,
    });
    setAllStructures(data?.feesstructure || []);
  }, [selectedAcYear]);

  useEffect(() => { loadStructures(); }, [loadStructures]);

  const loadDetail = useCallback(async (cls, mode) => {
    if (!cls) return;
    setDetailLoading(true);
    const std = cls.standard.trim();
    const sec = cls.section.trim();
    const acYear = cls.acYear || selectedAcYear;
    const [totData, structData, txData] = await Promise.all([
      InvokeApi(Config.main_url + apiList.getFeesTotal, { standard: std, section: sec, academic_year: acYear }),
      InvokeApi(Config.main_url + apiList.getFeesStructureTotal, { standard: std, section: sec, academic_year: acYear }),
      InvokeApi(Config.main_url + apiList.getFeesMain, { standard: std, section: sec, academic_year: acYear, status: mode }),
    ]);
    setFeesData(totData?.fees || {});
    setStructureData(structData?.feesstructure?.[0] || structData?.feesstructure || {});
    setTransactions(txData?.fees || []);
    setDetailLoading(false);
  }, [selectedAcYear]);

  useEffect(() => {
    if (selectedClass) loadDetail(selectedClass, txMode);
  }, [selectedClass, txMode, loadDetail]);

  const handleSave = async () => {
    if (!form.standard || !form.section) { alert('Standard and section are required'); return; }
    setSaving(true);
    const data = await InvokeApi(Config.main_url + apiList.addFeesStructure, {
      ...form,
      registrationFee: parseFloat(form.registrationFee || 0),
      admissionFee: parseFloat(form.admissionFee || 0),
      transportFee: parseFloat(form.transportFee || 0),
      miscellaneousFee: parseFloat(form.miscellaneousFee || 0),
    });
    setSaving(false);
    if (data?.code === '200') {
      setDialog(false);
      loadStructures();
      if (selectedClass) loadDetail(selectedClass, txMode);
    } else alert(data?.message || 'Failed');
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this fee structure?')) return;
    const data = await InvokeApi(Config.main_url + apiList.deleteFeesStructure, { id: id, status: 'Inactive' });
    if (data?.code === '200') {
      setSelectedClass(null);   // go back to list — structure is gone
      setStructureData({});
      loadStructures();
    } else {
      alert(data?.message || 'Failed to delete');
    }
  };

  const f = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));
  const standards = [...new Set(sections.map(s => s.standard).filter(Boolean))].sort();
  const sectionsByStd = sections.filter(s => s.standard === form.standard).map(s => s.section);

  const getStructure = (std, sec) => allStructures.find(
    s => s.standard?.trim() === std?.trim() && s.section?.trim() === sec?.trim()
  );

  const totalFees = parseFloat(structureData?.total_fees || 0);
  const totalPaid = parseFloat(feesData?.totalFeesPaid || 0);
  const totalDue = Math.max(0, totalFees - totalPaid);
  const pct = totalFees > 0 ? Math.round((totalPaid / totalFees) * 100) : 0;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {selectedClass && (
            <Tooltip title="Back">
              <IconButton onClick={() => setSelectedClass(null)} sx={{ border: '1px solid #e8eaf6', borderRadius: 2 }}>
                <ArrowBack sx={{ color: '#1a237e' }} />
              </IconButton>
            </Tooltip>
          )}
          <Box>
            <Typography variant="h5" fontWeight={800} color="#1a237e">
              {selectedClass ? `${selectedClass.standard} - ${selectedClass.section} · Fees` : 'Fees Structure'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {selectedClass ? selectedAcYear : `${allStructures.length} structures · ${selectedAcYear}`}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Refresh"><IconButton onClick={loadStructures} sx={{ border: '1px solid #e8eaf6', borderRadius: 2, color: '#1a237e' }}><Refresh /></IconButton></Tooltip>
          <Button variant="contained" startIcon={<Add />} onClick={() => setDialog(true)}
            sx={{ background: 'linear-gradient(135deg,#1a237e,#1565c0)', borderRadius: 2, fontWeight: 700 }}>Add Structure</Button>
        </Box>
      </Box>

      {!selectedClass ? (
        <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', p: 3 }}>
          <ClassSectionGrid
            acYear={selectedAcYear}
            onAcYearChange={setSelectedAcYear}
            onSelect={(cls) => setSelectedClass(cls)}
            badge={(cls) => {
              const s = getStructure(cls.standard, cls.section);
              return s
                ? { label: `₹${parseFloat(s.total_fees || 0).toLocaleString()}`, bg: '#e8f5e9', color: '#2e7d32' }
                : { label: 'No Structure', bg: '#fff3e0', color: '#e65100' };
            }}
            subtitle={(cls) => {
              const s = getStructure(cls.standard, cls.section);
              return s ? 'per student' : 'Tap to add';
            }}
          />        </Card>
      ) : (
        detailLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}><CircularProgress sx={{ color: '#1a237e' }} /></Box>
        ) : (
          <>
            <Grid container spacing={2} mb={3}>
              {[
                { label: 'Total Fees', value: `₹${totalFees.toLocaleString()}`, gradient: 'linear-gradient(135deg,#1a237e,#1565c0)', sub: null },
                { label: 'Paid Amount', value: `₹${totalPaid.toLocaleString()}`, gradient: 'linear-gradient(135deg,#2e7d32,#388e3c)', sub: `${pct}% collected` },
                { label: 'Due Amount', value: `₹${totalDue.toLocaleString()}`, gradient: 'linear-gradient(135deg,#c62828,#d32f2f)', sub: null },
              ].map(card => (
                <Grid item xs={12} sm={4} key={card.label}>
                  <Card sx={{ borderRadius: 3, background: card.gradient, boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}>
                    <CardContent sx={{ p: 2.5, textAlign: 'center' }}>
                      <Typography sx={{ color: 'rgba(255,255,255,0.8)', fontSize: 13, mb: 0.5 }}>{card.label}</Typography>
                      <Typography sx={{ color: '#fff', fontSize: 28, fontWeight: 900 }}>{card.value}</Typography>
                      {card.sub && <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 12 }}>{card.sub}</Typography>}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {structureData?.registration_fee !== undefined && (
              <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', mb: 3 }}>
                <CardContent sx={{ p: 2.5 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                    <Typography fontWeight={700} color="#1a237e">Fee Breakdown</Typography>
                    <Tooltip title="Delete Structure">
                      <Button size="small" color="error" startIcon={<Delete />}
                        onClick={() => handleDelete(structureData.ID || structureData.id)}>
                        Delete
                      </Button>
                    </Tooltip>
                  </Box>
                  <Grid container spacing={1}>
                    {[
                      { label: 'Registration', value: structureData.registration_fee },
                      { label: 'Admission', value: structureData.admission_fee },
                      { label: 'Transport', value: structureData.transport_fee },
                      { label: 'Miscellaneous', value: structureData.miscellaneous_fee },
                    ].map(item => (
                      <Grid item xs={6} sm={3} key={item.label}>
                        <Box sx={{ p: 1.5, background: '#f5f6fa', borderRadius: 2, textAlign: 'center' }}>
                          <Typography fontSize={11} color="text.secondary">{item.label}</Typography>
                          <Typography fontSize={14} fontWeight={700} color="#1a237e">₹{parseFloat(item.value || 0).toLocaleString()}</Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </CardContent>
              </Card>
            )}

            <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
              <CardContent sx={{ p: 2.5 }}>
                <Box sx={{ display: 'flex', gap: 1, mb: 2.5, flexWrap: 'wrap' }}>
                  {STATUS_TABS.map(t => (
                    <Box key={t.key} onClick={() => setTxMode(t.key)} sx={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center', px: 2, py: 0.6,
                      borderRadius: 10, cursor: 'pointer', border: '1.5px solid',
                      borderColor: txMode === t.key ? t.color : '#e8eaf6',
                      background: txMode === t.key ? t.bg : '#fff',
                      transition: 'all 0.2s',
                    }}>
                      {/* <Box sx={{ color: txMode === t.key ? t.color : 'text.secondary' }}>{t.icon}</Box> */}
                      <Typography fontSize={13} fontWeight={700} color={txMode === t.key ? t.color : 'text.secondary'}>{t.label}</Typography>
                    </Box>
                  ))}
                </Box>

                <Typography fontWeight={700} color="#1a237e" mb={1.5}>Transactions</Typography>

                {transactions.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Receipt sx={{ fontSize: 48, color: '#e8eaf6', mb: 1 }} />
                    <Typography color="text.secondary">No {txMode.toLowerCase()} transactions</Typography>
                  </Box>
                ) : (
                  transactions.map((tx, i) => {
                    const txTotal = Number(tx.registration_fee || 0) + Number(tx.admission_fee || 0) + Number(tx.transport_fee || 0) + Number(tx.miscellaneous_fee || 0);
                    const tab = STATUS_TABS.find(t => t.key === tx.status) || STATUS_TABS[0];
                    return (
                      <Box key={i} sx={{ mb: 1.5, p: 2, borderRadius: 2, border: '1px solid #f0f0f0', borderLeft: `4px solid ${tab.color}`, background: '#fff', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1 }}>
                          <Box>
                            <Box sx={{ display: 'flex', gap: 1, mb: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                              <Typography fontSize={13} fontWeight={700} color="#1a237e">Receipt: {tx.recipt_no || '—'}</Typography>
                              <Chip label={tx.status} size="small" sx={{ background: tab.bg, color: tab.color, fontWeight: 700, fontSize: 10 }} />
                            </Box>
                            <Typography fontSize={12} color="text.secondary">{tx.recipt_name || '—'}</Typography>
                            <Typography fontSize={11} color="text.disabled">{tx.created_at ? dayjs(tx.created_at).format('DD MMM YYYY') : '—'}</Typography>
                          </Box>
                          <Box sx={{ textAlign: 'right' }}>
                            <Typography fontSize={18} fontWeight={900} color={tab.color}>₹{txTotal.toLocaleString()}</Typography>
                            {tx.fees_recipt_url && (
                              <Button size="small" variant="outlined" href={tx.fees_recipt_url} target="_blank"
                                sx={{ mt: 0.5, borderRadius: 2, fontSize: 11, color: '#1a237e', borderColor: '#1a237e' }}>
                                View Receipt
                              </Button>
                            )}
                          </Box>
                        </Box>
                      </Box>
                    );
                  })
                )}
              </CardContent>
            </Card>
          </>
        )
      )}

      <Dialog open={dialog} onClose={() => setDialog(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, color: '#1a237e' }}>Add Fees Structure</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField select fullWidth size="small" label="Standard *" value={form.standard} onChange={e => setForm(p => ({ ...p, standard: e.target.value, section: '' }))}>
                {standards.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField select fullWidth size="small" label="Section *" value={form.section} onChange={f('section')}>
                {sectionsByStd.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField select fullWidth size="small" label="Academic Year" value={form.academic_year} onChange={f('academic_year')}>
                {academicYears.map(y => <MenuItem key={y.value} value={y.value}>{y.label}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12}><Divider><Typography fontSize={12} color="text.secondary">Fee Breakdown</Typography></Divider></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Tuition Fee" type="number" value={form.registrationFee} onChange={f('registrationFee')} InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Admission Fee" type="number" value={form.admissionFee} onChange={f('admissionFee')} InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Transport Fee" type="number" value={form.transportFee} onChange={f('transportFee')} InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Miscellaneous Fee" type="number" value={form.miscellaneousFee} onChange={f('miscellaneousFee')} InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }} /></Grid>
            <Grid item xs={12}>
              <Box sx={{ p: 1.5, background: '#e8f5e9', borderRadius: 2 }}>
                <Typography fontSize={14} fontWeight={700} color="#2e7d32">
                  Total: ₹{(parseFloat(form.registrationFee || 0) + parseFloat(form.admissionFee || 0) + parseFloat(form.transportFee || 0) + parseFloat(form.miscellaneousFee || 0)).toLocaleString()}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, gap: 1 }}>
          <Button onClick={() => setDialog(false)} variant="outlined" sx={{ borderRadius: 2 }}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving}
            sx={{ background: 'linear-gradient(135deg,#1a237e,#1565c0)', borderRadius: 2, fontWeight: 700 }}>
            {saving ? <CircularProgress size={20} color="inherit" /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
